import { useEffect, useState, useRef } from "react";
import axios from "axios";
import {
    BookOpen, Star, FileText, TrendingUp,
    Clock, Lock, CheckCircle
} from "react-feather";
import "./style.css";
import { useAuth, useMessage } from "../../global-components/src";
import { PACOCA_API_URL, READBOOKS_API_URL } from "../../global-components/src/utils/vars";
import { errorMessage } from "../../global-components/src/utils/text";
import { BookMarked, Sparkles } from "lucide-react";
import Spinner from "../../global-components/src/components/spinner/spiner";
const fmt = (v: number) =>
    new Intl.NumberFormat("pt-BR", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
    }).format(v);
interface BookData {
    nome_livro: string;
    descricao_livro: string;
    paginas_lidas: number;
    total_paginas: number;
    data_inicio: string | null;
    data_termino: string | null;
    lido: string;
    tempo_lido: string;
    show_in_pacoca: boolean;
}

interface BooksResponse {
    books: BookData[];
    totalBooks: number;
    totalWishList: number;
    totalReadBooks: number;
    totalNotReadBooks: number;
}

const COVER_COLORS_LIGHT = [
    { bg: "#f5e4b8", fg: "#c8922a" },
    { bg: "#d4ebd4", fg: "#3a6b3a" },
    { bg: "#fae4dc", fg: "#b84a2a" },
    { bg: "#e4e8f5", fg: "#3a4a8b" },
    { bg: "#ede4f5", fg: "#7a3a9b" },
];

const COVER_COLORS_DARK = [
    { bg: "#3a2e10", fg: "#e8c85a" },
    { bg: "#1a3018", fg: "#6abf6a" },
    { bg: "#3a1a10", fg: "#e87a5a" },
    { bg: "#1a1e38", fg: "#7a8adb" },
    { bg: "#2e1a38", fg: "#c87adb" },
];

const GENRE_COLORS = ["#c8922a", "#3a6b3a", "#b84a2a", "#3a4a8b", "#7a3a9b"];

/* ------------------------------------------------------------------ */
/* Sub-components                                                        */
/* ------------------------------------------------------------------ */

function StatCard({
    icon,
    label,
    value,
    sub,
    variant,
}: {
    icon: React.ReactNode;
    label: string;
    value: string | number;
    sub?: string;
    variant: "gold" | "moss" | "rust" | "neutral";
}) {
    return (
        <div className="rd-stat-card">
            <div className={`rd-stat-icon rd-stat-icon--${variant}`}>{icon}</div>
            <span className="rd-stat-label">{label}</span>
            <span className="rd-stat-value">{value}</span>
            {sub && <span className="rd-stat-sub">{sub}</span>}
        </div>
    );
}

function BookRow({ book, index }: { book: BookData; index: number }) {
    const isDark = document.documentElement.classList.contains("dark");
    const palette = isDark ? COVER_COLORS_DARK : COVER_COLORS_LIGHT;
    const color = palette[index % palette.length];
    const pct = parseInt(book.tempo_lido) || 0;
    const done = book.lido === "sim";

    return (
        <div className="rd-book-row">
            <div
                className="rd-book-cover"
                style={{ background: color.bg, color: color.fg }}
                aria-hidden="true"
            >
                {book.nome_livro.trim()[0] ?? "?"}
            </div>
            <div className="rd-book-info">
                <span className="rd-book-name" title={book.nome_livro}>
                    {book.nome_livro}
                </span>
                <span className="rd-book-meta">
                    {fmt(book.paginas_lidas)}/{fmt(book.total_paginas)} páginas
                    {done ? (
                        <span className="rd-book-done">
                            <CheckCircle size={10} /> Concluído
                        </span>
                    ) : (
                        " • em leitura"
                    )}
                </span>
                <div className="rd-progress-wrap" role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100}>
                    <div
                        className={`rd-progress-bar ${done ? "rd-progress-bar--done" : ""}`}
                        style={{ width: `${pct}%` }}
                    />
                </div>
            </div>
            <span className="rd-book-pct">{pct}%</span>
        </div>
    );
}

function GenreBar({ name, count, max, colorIndex }: { name: string; count: number; max: number; colorIndex: number }) {
    return (
        <div className="rd-genre-item">
            <span className="rd-genre-name">{name}</span>
            <div className="rd-genre-bar-wrap">
                <div
                    className="rd-genre-bar"
                    style={{ width: `${Math.round((count / max) * 100)}%`, background: GENRE_COLORS[colorIndex % GENRE_COLORS.length] }}
                />
            </div>
            <span className="rd-genre-pct">
                {count} livro{count !== 1 ? "s" : ""}
            </span>
        </div>
    );
}

function ActivityDots({ books }: { books: BookData[] }) {
    // Build a simple 28-day activity heatmap based on reading start/end dates
    const today = new Date();
    const dots = Array.from({ length: 28 }, (_, i) => {
        const day = new Date(today);
        day.setDate(today.getDate() - (27 - i));
        const iso = day.toISOString().split("T")[0];
        const hasActivity = books.some((b) => {
            if (!b.data_inicio) return false;
            const start = b.data_inicio.split("T")[0];
            const end = b.data_termino ? b.data_termino.split("T")[0] : null;
            return iso >= start && (end === null || iso <= end);
        });
        return hasActivity ? (i < 21 ? "done" : "active") : "";
    });

    return (
        <div>
            <div className="rd-activity-dots">
                {dots.map((cls, i) => (
                    <div key={i} className={`rd-day-dot ${cls ? `rd-day-dot--${cls}` : ""}`} />
                ))}
            </div>
            <div className="rd-month-labels">
                <span>4 sem atrás</span>
                <span>3 sem</span>
                <span>2 sem</span>
                <span>Esta semana</span>
            </div>
        </div>
    );
}

function ReaderBadge({ rate }: { rate: number }) {
    if (rate >= 80) return <span className="rd-badge rd-badge--fast">Leitor ávido</span>;
    if (rate >= 50) return <span className="rd-badge rd-badge--reader">Leitor consistente</span>;
    return <span className="rd-badge rd-badge--casual">Leitor casual</span>;
}

function GenreChart({ books }: { books: BookData[] }) {
    const genres: Record<string, number> = {};
    books
        .filter((b) => b.data_inicio)
        .forEach((b) => {
            const g = (b.descricao_livro || "Outro").split("/")[0].trim() || "Outro";
            genres[g] = (genres[g] || 0) + 1;
        });
    const sorted = Object.entries(genres).sort((a, b) => b[1] - a[1]).slice(0, 5);
    const max = sorted[0]?.[1] || 1;

    if (!sorted.length)
        return <p className="rd-empty">Nenhum dado de gênero disponível.</p>;

    return (
        <div className="rd-genre-list">
            {sorted.map(([name, count], i) => (
                <GenreBar key={name} name={name} count={count} max={max} colorIndex={i} />
            ))}
        </div>
    );
}

/* Monthly bar chart — no external lib */
function MonthlyChart({ books }: { books: BookData[] }) {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const [isDark, setIsDark] = useState(() => document.documentElement.classList.contains("dark"));

    useEffect(() => {
        const observer = new MutationObserver(() => {
            setIsDark(document.documentElement.classList.contains("dark"));
        });
        observer.observe(document.documentElement, { attributes: true, attributeFilter: ["class"] });
        return () => observer.disconnect();
    }, []);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext("2d");
        if (!ctx) return;

        // Read theme-aware colors from CSS variables
        const style = getComputedStyle(document.documentElement);
        const colorGold = style.getPropertyValue("--rd-gold").trim() || "#c8922a";
        const colorInk2 = style.getPropertyValue("--rd-ink2").trim() || "#4a4843";
        const colorInk3 = style.getPropertyValue("--rd-ink3").trim() || "#8a8880";

        const finished = books.filter((b) => b.data_termino);
        const monthly: Record<string, number> = {};
        finished.forEach((b) => {
            const d = new Date(b.data_termino!);
            const key = d.toLocaleDateString("pt-BR", { month: "short", year: "2-digit" });
            monthly[key] = (monthly[key] || 0) + 1;
        });

        let labels = Object.keys(monthly);
        let values = Object.values(monthly);

        if (!labels.length) {
            labels = ["Jan/24", "Fev/24", "Mar/24"];
            values = [0, 0, 0];
        }

        const W = canvas.width;
        const H = canvas.height;
        const padding = { top: 16, right: 16, bottom: 32, left: 32 };
        const chartW = W - padding.left - padding.right;
        const chartH = H - padding.top - padding.bottom;
        const maxVal = Math.max(...values, 1);
        const barW = Math.min(36, (chartW / labels.length) * 0.6);
        const gap = chartW / labels.length;

        ctx.clearRect(0, 0, W, H);

        // grid lines
        ctx.strokeStyle = "rgba(136,130,120,0.12)";
        ctx.lineWidth = 0.5;
        for (let i = 0; i <= maxVal; i++) {
            const y = padding.top + chartH - (i / maxVal) * chartH;
            ctx.beginPath();
            ctx.moveTo(padding.left, y);
            ctx.lineTo(padding.left + chartW, y);
            ctx.stroke();
        }

        // bars
        labels.forEach((label, i) => {
            const val = values[i];
            const barH = (val / maxVal) * chartH;
            const x = padding.left + i * gap + gap / 2 - barW / 2;
            const y = padding.top + chartH - barH;

            ctx.fillStyle = colorGold;
            ctx.beginPath();
            ctx.roundRect(x, y, barW, barH, [4, 4, 0, 0]);
            ctx.fill();

            // label
            ctx.fillStyle = colorInk3;
            ctx.font = "10px DM Sans, sans-serif";
            ctx.textAlign = "center";
            ctx.fillText(label, x + barW / 2, H - 8);

            // value
            if (val > 0) {
                ctx.fillStyle = colorInk2;
                ctx.font = "500 11px DM Sans, sans-serif";
                ctx.fillText(String(val), x + barW / 2, y - 5);
            }
        });
    }, [books, isDark]);

    return (
        <canvas
            ref={canvasRef}
            width={600}
            height={180}
            style={{ width: "100%", height: "180px" }}
            role="img"
            aria-label="Gráfico de livros concluídos por mês"
        >
            Nenhum livro concluído ainda.
        </canvas>
    );
}

/* ------------------------------------------------------------------ */
/* AI Analysis section                                                  */
/* ------------------------------------------------------------------ */

function AiAnalysis({ books, isVerified, token, userId }: {
    books: BookData[];
    isVerified: boolean;
    token: string | null;
    userId?: number | string;
}) {
    const [state, setState] = useState<"idle" | "loading" | "done" | "error">("idle");
    const [analysis, setAnalysis] = useState("");

    const load = async () => {
        const cacheKey = userId ? `ai_reading_analysis_${userId}` : "ai_reading_analysis";
        const cachedAnalysis = localStorage.getItem(cacheKey);

        if (cachedAnalysis) {
            setAnalysis(cachedAnalysis);
            setState("done");
            return;
        }

        if (state === "loading") return;
        setState("loading");
        try {
            const url = `${READBOOKS_API_URL}/books/ai-summary${userId ? `?user_id=${userId}` : ''}`;
            const response = await axios.get(url, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    Accept: "application/json",
                },
            });

            const summary = response.data?.summary;
            if (summary) {
                setAnalysis(summary);
                setState("done");
                localStorage.setItem(cacheKey, summary);
            } else {
                setState("error");
            }
        } catch (err) {
            console.error("Erro ao carregar análise de IA:", err);
            setState("error");
        }
    };

    useEffect(() => {
        if (isVerified) load();
    }, [isVerified, userId]);

    if (!isVerified) {
        return (
            <div className="rd-ai-lock">
                <Lock size={24} className="rd-ai-lock-icon" />
                <p>A análise de leitura por IA está disponível para leitores verificados.</p>
            </div>
        );
    }

    if (state === "loading") {
        return (
            <div className="rd-ai-loading">
                <Spinner size="16px" />
                <span>Analisando seus hábitos de leitura…</span>
            </div>
        );
    }

    if (state === "error") {
        return (
            <p className="rd-ai-text">
                Não foi possível carregar a análise no momento. Tente novamente mais tarde.
            </p>
        );
    }

    return (
        <>
            {analysis.split("\n\n").filter(Boolean).map((p, i) => (
                <p key={i} className="rd-ai-text">
                    {p}
                </p>
            ))}
        </>
    );
}

/* ------------------------------------------------------------------ */
/* Main page                                                            */
/* ------------------------------------------------------------------ */

export default function ReadingDashboardPage() {
    const { token, user } = useAuth();
    const { setMessage } = useMessage();

    const [data, setData] = useState<BooksResponse | null>(null);
    const [loading, setLoading] = useState(true);

    // In a real app, `isVerified` would come from user profile data
    const isVerified: boolean = ((user as any)?.stamps?.includes("brown") || (user as any)?.stamps?.includes("blue") || (user as any)?.stamps?.includes("black") || (user as any)?.position > 0) ?? false;

    useEffect(() => {
        (async () => {
            try {
                const res = await axios.get(`${READBOOKS_API_URL}/books`, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                setData(res.data);
            } catch (err: any) {
                setMessage(errorMessage(err), "error");
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    if (loading) {
        return (
            <div className="rd-loading">
                <Spinner size="36px" />
            </div>
        );
    }

    const books = data?.books ?? [];
    const readBooks = books.filter((b) => b.lido === "sim");
    const inProgress = books.filter((b) => b.lido !== "sim" && b.data_inicio);
    const totalPages = books.reduce(
            (s, b) => s + Number(b.paginas_lidas || 0),
            0
        );
    const completionRate =
        data && data.totalBooks > 0
            ? Math.round((data.totalReadBooks / data.totalBooks) * 100)
            : 0;
    const avgPages =
        readBooks.length > 0
            ? Math.round(readBooks.reduce((s, b) => s + Number(b.total_paginas), 0) / readBooks.length)
            : 0;

    const displayBooks = [...inProgress, ...readBooks].slice(0, 6);

    return (
        <div className="rd-wrapper margin-top-menu container">
            {/* Header */}
            <div className="rd-header">
                <h1>Minha Leitura</h1>
                <p>Acompanhe seus hábitos e evolução como leitor</p>
            </div>

            {/* Stat cards */}
            <div className="rd-stats-grid">
                <StatCard
                    icon={<BookOpen size={18} />}
                    label="Total lidos"
                    value={data?.totalReadBooks ?? 0}
                    sub={`de ${data?.totalBooks ?? 0} livros`}
                    variant="gold"
                />
                <StatCard
                    icon={<BookMarked size={18} />}
                    label="Em andamento"
                    value={inProgress.length}
                    sub="leitura ativa"
                    variant="moss"
                />
                <StatCard
                    icon={<Star size={18} />}
                    label="Lista de desejos"
                    value={data?.totalWishList ?? 0}
                    sub="aguardando"
                    variant="rust"
                />
                <StatCard
                    icon={<FileText size={18} />}
                    label="Páginas lidas"
                    value={fmt(totalPages)}
                    sub="total"
                    variant="neutral"
                />
            </div>

            {/* Middle row: genres + rhythm */}
            <div className="rd-mid-grid">
                <div className="rd-section">
                    <div className="rd-section-title">
                        <TrendingUp size={14} /> Gêneros mais lidos
                    </div>
                    <GenreChart books={books} />
                </div>
                <div className="rd-section">
                    <div className="rd-section-title">
                        <TrendingUp size={14} /> Ritmo de leitura
                    </div>
                    <div className="rd-rhythm-list">
                        <div>
                            <span className="rd-insight-label">Taxa de conclusão</span>
                            <span className="rd-insight-value">{completionRate}%</span>
                        </div>
                        <div>
                            <span className="rd-insight-label">Páginas por livro</span>
                            <span className="rd-insight-value">{fmt(avgPages)} pág.</span>
                        </div>
                        <div>
                            <span className="rd-insight-label">Perfil de leitor</span>
                            <ReaderBadge rate={completionRate} />
                        </div>
                    </div>
                </div>
            </div>

            {/* Activity heatmap */}
            <div className="rd-section">
                <div className="rd-section-title">
                    <Clock size={14} /> Atividade de leitura — últimas semanas
                </div>
                <ActivityDots books={books} />
            </div>

            {/* Books in progress */}
            <div className="rd-section">
                <div className="rd-section-title">
                    <BookOpen size={14} /> Livros em progresso e recentes
                </div>
                {displayBooks.length === 0 ? (
                    <p className="rd-empty">Nenhum livro em leitura ainda.</p>
                ) : (
                    <div className="rd-books-list">
                        {displayBooks.map((b, i) => (
                            <BookRow key={i} book={b} index={i} />
                        ))}
                    </div>
                )}
            </div>

            {/* Monthly chart */}
            <div className="rd-section">
                <div className="rd-section-title">
                    <TrendingUp size={14} /> Livros concluídos por mês
                </div>
                <MonthlyChart books={readBooks} />
            </div>

            {/* AI Analysis */}
            <div className="rd-ai-section">
                <div className="rd-ai-header">
                    <span className="rd-ai-badge">
                        <Sparkles size={11} /> Análise da IA
                    </span>
                    {!isVerified && (
                        <span className="rd-ai-verified-hint">Exclusivo para verificados</span>
                    )}
                </div>
                <AiAnalysis
                    books={books}
                    isVerified={isVerified}
                    token={token}
                    userId={user?.id}
                />
            </div>
        </div>
    );
}